import React from 'react';
import { Truck, Package, RotateCcw, Clock, MapPin, AlertCircle } from 'lucide-react';
import Header from './components/Header2';
import Footer from './components/Footer2';

const ShippingAndExchange = () => {
    const shippingOptions = [
        { icon: Truck, title: 'Standard Shipping', time: '5-7 Business Days', cost: 'Free on orders above \u20B95,000' },
        { icon: Package, title: 'Express Shipping', time: '2-3 Business Days', cost: '\u20B9399' },
        { icon: MapPin, title: 'International', time: '10-15 Business Days', cost: 'Varies by destination' },
    ];

    const returnSteps = [
        { step: '01', title: 'Initiate Return', description: 'Go to your Orders page and select the item you wish to return within 7 days of delivery.' },
        { step: '02', title: 'Schedule Pickup', description: 'Choose a convenient pickup date and time. Our delivery partner will collect the package from your doorstep.' },
        { step: '03', title: 'Quality Check', description: 'Once we receive the item, our team verifies it is in original condition with tags attached.' },
        { step: '04', title: 'Refund Processed', description: 'Refund is initiated within 3 business days of verification. Credited to original payment method in 7-10 days.' },
    ];

    const policies = [
        'Returns accepted within 7 days of delivery',
        'Items must be unused, unwashed, with original tags',
        'Bridal and custom-made pieces are non-returnable',
        'Free size exchanges for standard items',
        'Refunds processed to original payment method',
        'COD refunds via bank transfer (NEFT)',
    ];

    return (
        <div className="w-full min-h-screen bg-[#0A0A0A]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
            <Header />

            <main className="pt-[90px] md:pt-[110px] pb-16">
                {/* Hero */}
                <div className="px-4 md:px-6 pb-8 md:pb-12 text-center border-b border-[#2A2A2A]">
                    <Truck className="w-10 h-10 text-[#FF6B35] mx-auto mb-4" />
                    <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-wider mb-3 text-white font-['Montserrat']">
                        Shipping & Returns
                    </h1>
                    <p className="text-sm text-[#A3A3A3] font-['Montserrat']">Everything you need to know about delivery and returns</p>
                </div>

                <div className="max-w-[900px] mx-auto px-4 md:px-6 pt-10">
                    {/* Shipping Options */}
                    <div className="mb-12">
                        <h2 className="text-lg font-bold text-white mb-6 font-['Montserrat'] uppercase tracking-wider">
                            Shipping Options
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {shippingOptions.map(({ icon: Icon, title, time, cost }) => (
                                <div key={title} className="bg-[#1A1A1A] border border-[#2A2A2A] p-6 text-center hover:border-[#FF6B35] transition-colors">
                                    <Icon className="w-8 h-8 text-[#FF6B35] mx-auto mb-3" />
                                    <h3 className="text-sm font-bold text-white mb-2 font-['Montserrat'] uppercase">{title}</h3>
                                    <p className="text-xs text-[#A3A3A3] mb-1 font-['Montserrat']">{time}</p>
                                    <p className="text-xs font-semibold text-[#FF6B35] font-['Montserrat']">{cost}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Return Process */}
                    <div className="mb-12">
                        <h2 className="text-lg font-bold text-white mb-6 font-['Montserrat'] uppercase tracking-wider">
                            Return Process
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {returnSteps.map(({ step, title, description }) => (
                                <div key={step} className="bg-[#1A1A1A] border border-[#2A2A2A] p-6 flex gap-4">
                                    <div className="w-10 h-10 flex items-center justify-center bg-[#FF6B35] text-white text-sm font-bold flex-shrink-0 font-['Montserrat']">
                                        {step}
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-bold text-white mb-1 font-['Montserrat']">{title}</h3>
                                        <p className="text-xs text-[#A3A3A3] leading-relaxed font-['Montserrat']">{description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Return Policy */}
                    <div className="mb-12">
                        <h2 className="text-lg font-bold text-white mb-6 font-['Montserrat'] uppercase tracking-wider">
                            Return Policy
                        </h2>
                        <div className="bg-[#1A1A1A] border border-[#2A2A2A] p-6">
                            <ul className="space-y-3">
                                {policies.map((policy, i) => (
                                    <li key={i} className="flex items-start gap-3 text-sm text-[#A3A3A3] font-['Montserrat']">
                                        <span className="w-1.5 h-1.5 bg-[#FF6B35] rounded-full mt-1.5 flex-shrink-0" />
                                        {policy}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {/* Note */}
                    <div className="bg-[#FF6B35]/5 border border-[#FF6B35]/20 p-5 flex gap-3">
                        <AlertCircle className="w-5 h-5 text-[#FF6B35] flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-semibold text-[#FF6B35] mb-1 font-['Montserrat']">Important Note</p>
                            <p className="text-xs text-[#A3A3A3] leading-relaxed font-['Montserrat']">
                                Handcrafted items may have slight variations in colour, pattern, and dimensions. These are characteristics of artisanal craftsmanship and not defects. For any concerns, please contact us at hello@rang.in.
                            </p>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default ShippingAndExchange;
